<script setup>
import { computed, ref } from 'vue'
import axios from 'axios'
import Button from 'primevue/button'
import FeatureBlocker from '../FeatureBlocker.vue'
import NotebookSidebar from './NotebookSidebar.vue'
import NotebookSectionEditor from './NotebookSectionEditor.vue'

const props = defineProps({
    contactId: { type: [Number, String], required: true },
    initialSections: { type: Array, default: () => [] },
})

const sections = ref(props.initialSections)
const activeId = ref(props.initialSections[0]?.id ?? null)

const activeSection = computed(() => sections.value.find((s) => s.id === activeId.value) ?? null)

const baseUrl = `/app/contacts/${props.contactId}/notebook`

const suggestedTitles = ['Alergie', 'Preference', 'Historie', 'VIP info']

async function createSection(title) {
    const { data } = await axios.post(baseUrl, { title })
    sections.value.push(data)
    activeId.value = data.id
}

async function renameSection({ id, title }) {
    const { data } = await axios.patch(`${baseUrl}/${id}`, { title })
    const section = sections.value.find((s) => s.id === id)
    if (section) {
        section.title = data.title
    }
}

async function saveSection({ id, content }) {
    const { data } = await axios.patch(`${baseUrl}/${id}`, { content })
    const section = sections.value.find((s) => s.id === id)
    if (section) {
        section.content = data.content
    }
}

async function deleteSection(id) {
    await axios.delete(`${baseUrl}/${id}`)
    sections.value = sections.value.filter((s) => s.id !== id)
    if (activeId.value === id) {
        activeId.value = sections.value[0]?.id ?? null
    }
}

async function reorderSections(order) {
    order.forEach(({ id, sort_order }) => {
        const section = sections.value.find((s) => s.id === id)
        if (section) {
            section.sort_order = sort_order
        }
    })
    sections.value.sort((a, b) => a.sort_order - b.sort_order)
    await axios.post(`${baseUrl}/reorder`, { order })
}
</script>

<template>
    <FeatureBlocker name="roori_client_notebook" version="Pro" class="flex-1 min-h-0">
        <div v-if="sections.length" class="flex gap-3 h-full min-h-0">
            <NotebookSidebar
                :sections="sections"
                :active-id="activeId"
                @select="activeId = $event"
                @create="createSection($t('contacts.notebook_new_section_default'))"
                @rename="renameSection"
                @delete="deleteSection"
                @reorder="reorderSections"
            />
            <NotebookSectionEditor
                v-if="activeSection"
                :section="activeSection"
                fill
                @save="saveSection"
            />
        </div>

        <div v-else class="flex flex-col items-center justify-center gap-3 py-12 text-center">
            <i class="pi pi-book !text-3xl text-neutral-300"></i>
            <p class="text-sm text-neutral-500">{{ $t('contacts.notebook_empty_title') }}</p>
            <div class="flex gap-2 flex-wrap justify-center">
                <Button
                    v-for="title in suggestedTitles"
                    :key="title"
                    size="small"
                    outlined
                    :label="title"
                    @click="createSection(title)"
                />
            </div>
            <Button
                :label="$t('contacts.notebook_create_first')"
                icon="pi pi-plus"
                text
                @click="createSection($t('contacts.notebook_new_section_default'))"
            />
        </div>
    </FeatureBlocker>
</template>
